import React, { useState, useEffect, useRef, useNavigate } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Navbar from "./navbar";
import Footer from "./footer";
import image9 from "../assets/img9.avif";
import image10 from "../assets/img10.avif";
import image11 from "../assets/img11.avif";
import image12 from "../assets/img12.avif";

const stepImages = [image9, image10, image11, image12];

const HowItWorks = () => {
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const dropdownRef = useRef(null);
  // useEffect(() => {
  //   // Initialize language from localStorage or default to 'en'
  //   const storedLanguage = localStorage.getItem("language");
  //   const languageToUse = storedLanguage && ['en', 'ta'].includes(storedLanguage) ? storedLanguage : 'en';

  //   if (i18n.language !== languageToUse) {
  //     i18n.changeLanguage(languageToUse);
  //   }

  //   if (!storedLanguage) {
  //     localStorage.setItem("language", languageToUse);
  //   }

  //   setCurrentLanguage(languageToUse);
  // }, [i18n]);

  // Listen for language changes from other components
  useEffect(() => {
    const handleI18nLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      localStorage.setItem("language", lng);
    };

    i18n.on("languageChanged", handleI18nLanguageChange);

    return () => {
      i18n.off("languageChanged", handleI18nLanguageChange);
    };
  }, [i18n]);

  // Listen for storage changes and custom events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (
        e.key === "language" &&
        e.newValue &&
        ["en", "ta"].includes(e.newValue)
      ) {
        if (i18n.language !== e.newValue) {
          i18n.changeLanguage(e.newValue);
          setCurrentLanguage(e.newValue);
        }
      }
    };

    const handleCustomLanguageChange = (e) => {
      if (
        e.detail &&
        e.detail.language &&
        ["en", "ta"].includes(e.detail.language)
      ) {
        if (i18n.language !== e.detail.language) {
          i18n.changeLanguage(e.detail.language);
          setCurrentLanguage(e.detail.language);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChanged", handleCustomLanguageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleCustomLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = async (lang) => {
    try {
      await i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);
      setCurrentLanguage(lang);
      setShowLanguageDropdown(false);

      // Trigger a custom event to notify other components
      window.dispatchEvent(
        new CustomEvent("languageChanged", { detail: { language: lang } }),
      );

      // Optional: Force re-render by updating a timestamp
      window.dispatchEvent(new CustomEvent("forceUpdate"));
    } catch (error) {
      console.error("Error changing language:", error);
    }
  };

  // Ensure steps is an array
  const steps = t("steps", { returnObjects: true });
  const stepsArray = Array.isArray(steps) ? steps : [];

  return (
    <>
     <div className="fixed inset-0 bg-gradient-to-br from-orange-100 via-violet-200 to-indigo-300 bg-[length:400%_400%] animate-gradient -z-10" />
      <Navbar />

      <div className="flex-grow py-12 px-4 sm:px-6 lg:px-8 text-gray-800">
        <div className="max-w-7xl mx-auto">
          {/* Language Toggle Dropdown */}
          {/* <div className="flex justify-end mb-6" ref={dropdownRef}>
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="bg-white text-[#d35400] px-4 py-2 rounded-lg font-medium border border-[#f39c12] hover:bg-[#f39c12] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f39c12] transition-all duration-150 ease-in-out"
            >
              {t('languages.' + currentLanguage, { defaultValue: currentLanguage === 'en' ? 'English' : 'Tamil' })}
            </button>
            {showLanguageDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-10 w-40 bg-white rounded-lg shadow-lg border border-[#f39c12]/20 z-50"
              >
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`w-full text-left px-4 py-2 hover:bg-[#f39c12] hover:text-white rounded-t-lg transition-colors ${
                    currentLanguage === 'en' ? 'bg-[#f39c12] text-white' : 'text-[#d35400]'
                  }`}
                >
                  {t('languages.english', { defaultValue: 'English' })}
                </button>
                <button
                  onClick={() => handleLanguageChange('ta')}
                  className={`w-full text-left px-4 py-2 hover:bg-[#f39c12] hover:text-white rounded-b-lg transition-colors ${
                    currentLanguage === 'ta' ? 'bg-[#f39c12] text-white' : 'text-[#d35400]'
                  }`}
                >
                  {t('languages.tamil', { defaultValue: 'Tamil' })}
                </button>
              </motion.div>
            )}
          </div> */}

          <div className="text-center mt-16 mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#d35400] mb-4">
              {t("howItWorks", { defaultValue: "How It Works" })}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t("subtitle", { defaultValue: "Learn how our platform works" })}
            </p>
          </div>

          <div className="space-y-16 md:space-y-20">
            {stepsArray.map((step, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-6 md:gap-10 lg:gap-12 transition-all duration-300 hover:scale-[1.01]`}
              >
                <div className="relative w-full md:w-1/2 group">
                  <img
                    src={stepImages[index]}
                    alt={step?.title || `Step ${index + 1}`}
                    className="w-full rounded-xl md:rounded-2xl shadow-lg md:shadow-xl object-cover h-60 sm:h-72 md:h-80 lg:h-96 transition-all duration-500 group-hover:shadow-2xl group-hover:brightness-90"
                  />
                  <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 bg-[#e67e22] text-white text-2xl sm:text-3xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-full shadow-lg transform group-hover:scale-110 transition-all duration-300">
                    {step?.icon || "📱"}
                  </div>
                </div>

                <div className="w-full md:w-1/2 text-center md:text-left">
                  <div className="max-w-md mx-auto md:mx-0">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#e67e22] mb-3 sm:mb-4 flex items-center justify-center md:justify-start">
                      <span className="mr-2 sm:mr-3">{step?.icon || "📱"}</span>
                      {step?.title || `Step ${index + 1}`}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                      {step?.description || "Description not available"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 md:mt-20 text-center">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#d35400] mb-6">
              {t("ctaTitle", { defaultValue: "Ready to get started?" })}
            </h3>
            <a
              href="/register"
              className="inline-block bg-gradient-to-r from-[#f39c12] to-[#d35400] text-white py-2 px-6 rounded-lg font-medium hover:from-[#f39c12]/90 hover:to-[#d35400]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f39c12] transition-all duration-150 ease-in-out"
            >
              {t("ctaButton", { defaultValue: "Get Started" })}
            </a>
          </div>
        </div>
         
      </div>

     <Footer />
   
    </>
  );
};

export default HowItWorks;
